import { css } from '@emotion/css';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as KatrinaIcon } from 'src/assets/images/fortune/katrina.svg';
import { reveal } from 'src/components/shared/keyframe-animations';
import { RootState } from 'src/features';
import { actions } from 'src/features/fortune';
import { FortuneAnimation } from './FortuneAnimation';
import { PointResultBalloon } from './PointResultBalloon';

type FortuneHomePointResultProps = {
  pointFactor: number; // 今日の獲得ポイント倍率
  current: number; // 現在の所持ポイント
  commentId: string; //今日のひとことのID
};

export const FortuneHomePointResult: React.FC<FortuneHomePointResultProps> = ({
  pointFactor,
  commentId
}) => {
  const { t } = useTranslation('fortune');
  const updateStatus = useSelector((state: RootState) => state.fortune.updateStatus);
  const dispatch = useDispatch();

  useEffect(() => {
    return () => {
      dispatch(actions.animationEnd());
    };
  }, [dispatch]);

  const handleComplete = useCallback(() => {
    dispatch(actions.animationEnd());
  }, [dispatch]);

  if (updateStatus?.state === 'ok' && !updateStatus.result.animationEnd) {
    return <FortuneAnimation pointFactor={pointFactor} onComplete={handleComplete} />;
  }

  return (
    <div
      className={css`
        width: 100%;
        max-width: 480px;
        margin: 0 auto;
        padding: 0 16px;
      `}
    >
      <div
        className={css`
          display: flex;
          flex-direction: column;
          align-items: center;

          .balloon {
            margin: 16px;
          }
          .advice-area {
            min-width: 200px;
            background: #e5e7d9;
            border-radius: 10px;
            text-align: center;
            margin: 32px 0;
            padding: 0 16px;
          }
          .advice-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            transform: translateY(-18px);
          }
          .todays-advice {
            font-size: 12px;
            color: #8937ea;
            margin: 0 0 16px;
          }
          .advice {
            font-size: 14px;
            color: #4d3906;
            margin: 0 0 16px;
          }
          .katrina-conclusion {
            font-size: 12px;
            color: #938666;
          }

          @media (prefers-reduced-motion: no-preference) {
            .balloon.anim {
              visibility: hidden;
              opacity: 0;
              transform: scale(0.85);
              animation: 0.2s cubic-bezier(0.17, 0.89, 0.56, 1.72) ${reveal} both;
              animation-delay: 0.1s;
            }
            .advice-area.anim {
              transform: translateY(4px);
              opacity: 0;
              animation: 0.4s ease-in-out ${reveal} both;
              animation-delay: 1s;
            }
          }
        `}
      >
        <div className={`balloon ${updateStatus ? 'anim' : ''}`}>
          <PointResultBalloon pointFactor={pointFactor} />
        </div>
        <div className={`advice-area ${updateStatus ? 'anim' : ''}`}>
          <div className="advice-container">
            <KatrinaIcon style={{ width: '36px', height: '36px', transform: 'rotate(4deg)' }} />
            <p className="todays-advice">{t('todays_advice')}</p>
            <div className="advice">
              <p>{t(commentId)}</p>
            </div>
            <p className="katrina-conclusion">{t('katrina_conclusion')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};
